/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useMemo, useCallback, useRef } from "react";
import { Box, IndexTable, Text, Button, InlineStack } from "@medentic/polaris-react";
import { HideIcon, ViewIcon } from "@medentic/polaris-icons";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import PatientCellRenderer, { type TagsById } from "./PatientCellRenderer";
import { Column } from "../types";
import "../styles.scss";
interface EditColumnsProps {
  data: any[];
  columns?: Column[];
  onColumnsChange: (columns: Column[]) => void;
  tagsById?: TagsById;
}

const fixedColumns = ["core_avatar", "tags"];

/**
 * Draggable + Droppable item interface for react-dnd
 */
interface DragItem {
  type: string;
  id: string; // column.name
  index: number; // index in the columns array
  lastMoveTime?: number;
}

/**
 * SortableColumn with react-dnd
 */
function SortableColumn({
  column,
  index,
  data,
  moveColumn,
  changeVisibility,
  rowMarkup,
}: {
  column: Column;
  index: number;
  data: any[];
  moveColumn: (dragIndex: number, hoverIndex: number) => void;
  changeVisibility: (column: Column, selected: boolean) => void;
  rowMarkup: () => React.ReactElement[];
}) {
  const ref = useRef<HTMLDivElement>(null);

  // Don't allow drag for core_avatar column
  const isAvatarColumn = column.name === "core_avatar";

  const [{ isDragging }, drag] = useDrag<
    DragItem,
    void,
    { isDragging: boolean }
  >(
    () => ({
      type: "COLUMN",
      item: { type: "COLUMN", id: column.name, index },
      collect: (monitor) => ({
        isDragging: !!monitor.isDragging(),
      }),
      canDrag: !isAvatarColumn, // Prevent dragging for core_avatar
    }),
    [index, column.name, isAvatarColumn]
  );

  const [, drop] = useDrop<DragItem>(
    () => ({
      accept: "COLUMN",
      hover: (item, monitor) => {
        if (!ref.current) return;

        const dragIndex = item.index;
        const hoverIndex = index;

        // Prevent dropping to index 0 (avatar column position)
        if (hoverIndex === 0) return;
        if (dragIndex === hoverIndex) return;

        // Calculate x offsets
        const hoverBoundingRect = ref.current?.getBoundingClientRect();
        const clientOffset = monitor.getClientOffset();
        if (!clientOffset) return;

        const hoverClientX = clientOffset.x - hoverBoundingRect.left;
        const hoverWidth = hoverBoundingRect.right - hoverBoundingRect.left;

        // Trigger reorder when dragging past 25% of the column width
        const threshold = hoverWidth * 0.25;

        // If dragging to the right
        if (dragIndex < hoverIndex && hoverClientX > threshold) {
          // Prevent multiple reorders in quick succession
          if (Date.now() - (item.lastMoveTime || 0) > 25) {
            moveColumn(dragIndex, hoverIndex);
            item.index = hoverIndex;
            item.lastMoveTime = Date.now();
          }
        }
        // If dragging to the left
        else if (
          dragIndex > hoverIndex &&
          hoverClientX < hoverWidth - threshold
        ) {
          // Prevent multiple reorders in quick succession
          if (Date.now() - (item.lastMoveTime || 0) > 25) {
            moveColumn(dragIndex, hoverIndex);
            item.index = hoverIndex;
            item.lastMoveTime = Date.now();
          }
        }
      },
    }),
    [index, moveColumn]
  );

  const setColumnRef = useCallback(
    (node: HTMLDivElement | null) => {
      ref.current = node;
      if (node && !isAvatarColumn) {
        drag(drop(node));
      }
    },
    [drag, drop, isAvatarColumn]
  );

  const opacity = isDragging ? 0.4 : 1.0;

  return (
    <div
      ref={setColumnRef}
      className={`edit-column ${column.visible ? "" : "disabled"} ${
        isAvatarColumn ? "disabled" : ""
      } ${isDragging ? "dragging" : ""}`}
      style={{ cursor: isAvatarColumn ? "not-allowed" : "move", opacity }}
    >
      <Box borderRadius="200" borderColor="border-disabled" borderWidth="025">
        <IndexTable
          itemCount={data.length}
          selectedItemsCount={0}
          onSelectionChange={() => {}}
          headings={[
            {
              id: column.label,
              title: (
                <div style={{ width: "100%" }}>
                  <Box width="100%">
                    <InlineStack
                      gap={"400"}
                      align="space-between"
                      wrap={false}
                      blockAlign="center"
                    >
                      <Text as="span" variant="bodySm" fontWeight="semibold">
                        {column.label}
                      </Text>
                      {!fixedColumns.includes(column.name) && (
                        <Button
                          id={column.name}
                          size="micro"
                          variant="plain"
                          onClick={() => {
                            changeVisibility(column, !column.visible);
                          }}
                          icon={!column.visible ? HideIcon : ViewIcon}
                        />
                      )}
                    </InlineStack>
                  </Box>
                </div>
              ),
            },
          ]}
          selectable={false}
        >
          {rowMarkup()}
        </IndexTable>
      </Box>
    </div>
  );
}

/**
 * EditColumns component
 */
function EditColumns({
  data,
  columns = [],
  onColumnsChange,
  tagsById,
}: EditColumnsProps) {
  const moveColumn = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      // Prevent moving any column to index 0
      if (hoverIndex === 0) return;

      const updated = [...columns];
      const [removed] = updated.splice(dragIndex, 1);
      updated.splice(hoverIndex, 0, removed);

      // Update displayOrder for all columns
      const updatedWithOrder = updated.map((col, index) => ({
        ...col,
        displayOrder: index * 10, // Use multiples of 10 to allow for future insertions
      }));

      onColumnsChange(updatedWithOrder);
    },
    [columns, onColumnsChange]
  );

  const changeVisibility = useCallback(
    (column: Column, selected: boolean): void => {
      const updatedColumns = columns.map((c) => ({
        ...c,
        visible: c.name === column.name ? selected : c.visible,
      }));
      onColumnsChange(updatedColumns);
    },
    [columns, onColumnsChange]
  );

  const rowMarkup = useCallback(
    (column: Column) =>
      data.slice(0, 5).map((p, idx) => (
        <IndexTable.Row id={p.id} key={p.id} position={idx}>
          <IndexTable.Cell>
            <PatientCellRenderer column={column} patient={p} tagsById={tagsById} />
          </IndexTable.Cell>
        </IndexTable.Row>
      )),
    [data, tagsById]
  );

  // Update the sortedColumns useMemo to sort by displayOrder
  const sortedColumns = useMemo(() => {
    const sortedColumns = [...columns].sort(
      (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)
    );

    const avatarColumnIndex = sortedColumns.findIndex(
      (col) => col.name === "core_avatar"
    );
    if (avatarColumnIndex > 0) {
      const newColumns = [...sortedColumns];
      const [avatarColumn] = newColumns.splice(avatarColumnIndex, 1);
      newColumns.unshift(avatarColumn);
      return newColumns;
    }
    return sortedColumns;
  }, [columns]);

  const columnsMarkup = useMemo(
    () =>
      sortedColumns.map((column, index) => (
        <SortableColumn
          key={column.name}
          column={column}
          index={index}
          data={data}
          moveColumn={moveColumn}
          changeVisibility={changeVisibility}
          rowMarkup={() => rowMarkup(column)}
        />
      )),
    [sortedColumns, data, moveColumn, changeVisibility, rowMarkup]
  );

  return (
    <Box padding={"400"}>
      {/*
        DndProvider must wrap the entire region containing draggable and droppable items.
        We provide the HTML5Backend for typical mouse-based drag and drop.
      */}
      <DndProvider backend={HTML5Backend}>
        <div
          className="edit-columns-container"
          style={{ overflowX: "auto", overflowY: "hidden" }}
        >
          <InlineStack gap={"200"} wrap={false}>
            {columnsMarkup}
          </InlineStack>
        </div>
      </DndProvider>
    </Box>
  );
}

export default EditColumns;
